/**
 * Authentication Context for NGUI Voice Interface
 * Manages JWT tokens and user authentication state
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';

// App permissions interface
export interface AppPermissions {
  calendar: boolean;
  email: boolean;
  knowledge_base: boolean;
  cogito: boolean;
  tasks: boolean;
  disk: boolean;
  people: boolean;
  chat: boolean;
  contractors: boolean;
}

// User interface
export interface User {
  username: string;
  full_name: string;
  email: string;
  role?: string; // 'admin' or 'user'
  id?: number;
  status?: string;
  avatar_url?: string | null;
  app_permissions?: AppPermissions;
  voice_enabled?: boolean;
  avatar_enabled?: boolean;
  is_superadmin?: boolean;
  selected_avatar_id?: number | null;
}

// Auth context interface
interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  updateUser: (updates: Partial<User>) => void;
}

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider props
interface AuthProviderProps {
  children: ReactNode;
}

// Local storage keys
const TOKEN_KEY = 'ngui_auth_token';
const USER_KEY = 'ngui_auth_user';

/**
 * Authentication Provider Component
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // API base URL
  const API_BASE_URL = '/api';

  /**
   * Initialize auth state from local storage
   */
  useEffect(() => {
    const initAuth = async () => {
      try {
        const storedToken = localStorage.getItem(TOKEN_KEY);
        const storedUser = localStorage.getItem(USER_KEY);

        if (storedToken && storedUser) {
          setToken(storedToken);
          const parsedUser = JSON.parse(storedUser);
          setUser(parsedUser);

          // Fetch fresh profile with app_permissions
          try {
            const freshProfile = await axios.get(`${API_BASE_URL}/auth/profile`, {
              headers: { Authorization: `Bearer ${storedToken}` }
            });
            const updatedUser = freshProfile.data;
            localStorage.setItem(USER_KEY, JSON.stringify(updatedUser));
            setUser(updatedUser);
          } catch (profileError) {
            console.warn('Failed to refresh profile:', profileError);
            // Use stored user data if profile fetch fails
          }
        }
      } catch (error) {
        console.error('Failed to initialize auth:', error);
        // Clear invalid data
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, []);

  /**
   * Fetch user profile with app_permissions
   */
  const fetchProfile = async (authToken: string): Promise<User> => {
    const response = await axios.get(`${API_BASE_URL}/auth/profile`, {
      headers: { Authorization: `Bearer ${authToken}` }
    });
    return response.data;
  };

  /**
   * Login function
   */
  const login = async (username: string, password: string): Promise<void> => {
    try {
      setIsLoading(true);

      // Call login API
      const response = await axios.post(`${API_BASE_URL}/auth/login`, {
        username,
        password,
      });

      const { access_token, user: userData } = response.data;

      // Fetch full profile with app_permissions
      let fullUserData = userData;
      try {
        fullUserData = await fetchProfile(access_token);
      } catch (profileError) {
        console.warn('Failed to fetch profile, using login data:', profileError);
      }

      // Store token and user
      localStorage.setItem(TOKEN_KEY, access_token);
      localStorage.setItem(USER_KEY, JSON.stringify(fullUserData));

      setToken(access_token);
      setUser(fullUserData);
    } catch (error) {
      // Clear any existing data
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      setToken(null);
      setUser(null);

      // Re-throw error for component to handle
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Logout function
   */
  const logout = (): void => {
    // Clear local storage
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);

    // Clear state
    setToken(null);
    setUser(null);
  };

  /**
   * Update user data in state and localStorage without full re-auth
   */
  const updateUser = (updates: Partial<User>): void => {
    if (!user) return;
    const updatedUser = { ...user, ...updates };
    setUser(updatedUser);
    localStorage.setItem(USER_KEY, JSON.stringify(updatedUser));
  };

  const value: AuthContextType = {
    user,
    token,
    isAuthenticated: !!token && !!user,
    isLoading,
    login,
    logout,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Hook to use auth context
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
